import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import * as actions from "./gwsActions";

const EMPTY_FORM = { mode: "alias", inbound: "workspace", mailCname: true, dkim: "" };

/** Network config + this site's DNS matrix / verification status. */
function useGwsMatrix(site, connected) {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [matrix, setMatrix] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/gws/config").then((r) => setCfg(r.data)).catch(() => setCfg({ configured: false }));
  }, []);

  const refreshMatrix = useCallback(async () => {
    try {
      const r = await api.get(`/sites/${site.id}/gws/dns-matrix`);
      setMatrix(r.data.matrix || []); setStatus(r.data.status || null); setErr("");
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  }, [site.id]);

  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    (async () => { if (!cancelled) await refreshMatrix(); })();
    return () => { cancelled = true; };
  }, [connected, refreshMatrix]);

  return { cfg, status, setStatus, matrix, setMatrix, err, setErr, refreshMatrix };
}

/** All state + server actions for the per-site Google Workspace panel. */
export function useGwsPanel(site, reload) {
  const connected = Boolean(site.gws_connected_at);
  const m = useGwsMatrix(site, connected);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busyKind, setBusyKind] = useState(null);
  const [msg, setMsg] = useState("");
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Shared wrapper: busy flag + error surface + optional matrix/status update.
  async function run(kind, fn, { reloadSite = false } = {}) {
    setBusyKind(kind); m.setErr(""); setMsg("");
    try {
      const r = await fn();
      if (r?.data?.matrix) m.setMatrix(r.data.matrix);
      if (r?.data && "status" in r.data) m.setStatus(r.data.status || null);
      if (reloadSite) await reload();
      return r;
    } catch (e) { m.setErr(e.response?.data?.detail || e.message); return null; }
    finally { setBusyKind(null); }
  }

  const ctx = { site, reload, form, setField, run, setMsg, setStatus: m.setStatus, setMatrix: m.setMatrix, setErr: m.setErr };
  return {
    cfg: m.cfg, form, setField, busy: Boolean(busyKind), busyKind, msg, err: m.err, status: m.status, matrix: m.matrix, connected,
    verified: Boolean(m.status?.verified || site.gws_domain_status?.verified),
    dkimConfigured: Boolean(m.status ? m.status.dkim_configured : site.gws_dkim_value),
    currentInbound: m.status?.inbound || site.gws_inbound || "workspace",
    actions: {
      connect: () => actions.connect(ctx), fixDns: () => actions.fixDns(ctx), verifyNow: () => actions.verifyNow(ctx),
      saveDkim: () => actions.saveDkim(ctx), changeInbound: (v) => actions.changeInbound(ctx, v),
      disconnect: () => actions.disconnect(ctx), refreshMatrix: m.refreshMatrix,
    },
  };
}
